import React from 'react';
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';
import { Target, Compass, Flag, MapPin, ExternalLink, CheckCircle2 } from 'lucide-react';
import { COMPANY, CORE_VALUES, BRANCHES, TEAM } from '../mock';

export default function About() {
  return (
    <div>
      <Seo title="About Us | Fixed Group — 25+ Years of Cold Chain Excellence" description="Learn about Fixed Group — Malaysian specialists in reefer container services, cold-chain logistics and cold rooms since 1999. Vision, mission, values and team." />
      <PageHeader title="About Us" crumbs={[{ label: 'About Us' }]} />

      <section className="py-16">
        <div className="fx-container grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Who we are</span>
            <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2">{COMPANY.parent}</h2>
            <p className="mt-6 text-slate-600 leading-relaxed">{COMPANY.overview}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">With offices in Klang, Selangor, and Kota Kinabalu, Sabah, we continue to support major shipping lines, logistics hubs, and cold storage industries across Malaysia. We strive to deliver value through technical excellence and next-generation logistics technology.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1617938568125-b55e59775632?crop=entropy&cs=srgb&fm=jpg&q=85" alt="reefer" className="rounded-xl h-56 w-full object-cover" />
            <img src="https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85" alt="truck" className="rounded-xl h-56 w-full object-cover mt-8" />
            <img src="https://images.pexels.com/photos/14020705/pexels-photo-14020705.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="port" className="rounded-xl h-56 w-full object-cover -mt-4" />
            <img src="https://images.pexels.com/photos/30748602/pexels-photo-30748602.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="operations" className="rounded-xl h-56 w-full object-cover mt-4" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="fx-container grid md:grid-cols-3 gap-6">
          <div className="fx-card p-8">
            <div className="w-12 h-12 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center"><Compass className="text-emerald-600" size={22} /></div>
            <h3 className="mt-4 text-xl font-bold">Our Vision</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{COMPANY.vision}</p>
          </div>
          <div className="fx-card p-8">
            <div className="w-12 h-12 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center"><Target className="text-sky-600" size={22} /></div>
            <h3 className="mt-4 text-xl font-bold">Our Mission</h3>
            <ul className="mt-3 space-y-2 text-slate-600">
              {COMPANY.mission.map((m, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-1" /><span>{m}</span></li>
              ))}
            </ul>
          </div>
          <div className="fx-card p-8">
            <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center"><Flag className="text-amber-600" size={22} /></div>
            <h3 className="mt-4 text-xl font-bold">Our Goals</h3>
            <ul className="mt-3 space-y-2 text-slate-600">
              {COMPANY.goals.map((g, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-500 mt-1" /><span>{g}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="fx-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Culture</span>
            <h2 className="section-title center text-3xl md:text-4xl font-extrabold mt-2">Core Values</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORE_VALUES.map((v, i) => (
              <div key={i} className="fx-card p-6 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 flex items-center justify-center text-white font-extrabold">{i+1}</div>
                <h3 className="mt-4 font-bold text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="fx-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Our People</span>
            <h2 className="section-title center text-3xl md:text-4xl font-extrabold mt-2">Meet the Team</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {TEAM.map((t, i) => (
              <div key={i} className="fx-card p-6 text-center">
                <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 text-white flex items-center justify-center text-2xl font-extrabold shadow-inner">{t.initials}</div>
                <h3 className="mt-4 font-bold text-slate-900">{t.name}</h3>
                <p className="mt-1 text-sm text-emerald-600 font-medium">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="fx-container">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Locations</span>
            <h2 className="section-title center text-3xl md:text-4xl font-extrabold mt-2">Our Branches</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {BRANCHES.map((b) => (
              <div key={b.id} className="fx-card p-8">
                <div className="flex items-center gap-3"><MapPin className="text-emerald-600" size={22} /><h3 className="text-lg font-bold">{b.name}</h3></div>
                <p className="mt-3 text-slate-600 leading-relaxed">{b.address}</p>
                <a href={b.map} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-emerald-700 font-semibold hover:text-emerald-800">Open in Google Maps <ExternalLink size={14} /></a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
